import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CampaignControllerGetAwDefaultSettingsParams,
  CampaignControllerGetSimpleCampaignsListParams,
  CampaignControllerGetSimpleCampaignsListWithFoldersParams,
  CopyCampaignDto
} from 'src/api/Api';
import {
  campaignDuplicateCampaign,
  campaignGetAwDefaultSettings,
  campaignGetSimpleCampaignsList,
  campaignGetSimpleCampaignsListWithFolders
} from '@api/Campaign';

export function useCampaignGetAwDefaultSettings({
  params,
  enabled = true
}: {
  params: CampaignControllerGetAwDefaultSettingsParams;
  enabled?: boolean;
}) {
  const fetch = useQuery({
    queryKey: ['campaign', 'aw-default-settings', params.campaignId],
    enabled: !!params.campaignId && enabled,
    queryFn: async ({ signal }) => {
      const { data } = await campaignGetAwDefaultSettings(params, { signal });
      return data.settings;
    }
  });
  return fetch;
}

/** @deprecated Use separate queries for campaigns and folders instead */
export function useCampaignsListWithFoldersQuery({
  enabled = true,
  params
}: {
  params: CampaignControllerGetSimpleCampaignsListWithFoldersParams;
  enabled?: boolean;
}) {
  const campaignsListQuery = useQuery({
    // Not the simple list's key: the data has another shape.
    queryKey: [
      'campaigns',
      'list',
      'simple',
      params.workspaceId,
      'with-folders'
    ],
    enabled: !!params.workspaceId && enabled,
    queryFn: async () => {
      const { data } = await campaignGetSimpleCampaignsListWithFolders({
        workspaceId: params.workspaceId
      });
      return data?.list || [];
    },
    placeholderData: []
  });

  return campaignsListQuery;
}

export function useSimpleCampaignsListQuery({
  enabled = true,
  params
}: {
  params: CampaignControllerGetSimpleCampaignsListParams;
  enabled?: boolean;
}) {
  const campaignsListQuery = useQuery({
    queryKey: ['campaigns', 'list', 'simple', params.workspaceId],
    enabled: !!params.workspaceId && enabled,
    queryFn: async () => {
      const { data } = await campaignGetSimpleCampaignsList({
        workspaceId: params.workspaceId
      });
      return data?.list || [];
    },
    placeholderData: []
  });

  return campaignsListQuery;
}

export function useCampaignDuplicateCampaignMutation() {
  return useMutation({
    mutationFn: async (params: CopyCampaignDto) => {
      const { data } = await campaignDuplicateCampaign(params);
      return data;
    }
  });
}
